/**
 * Keeps a Command reply attached to its server-side run when the browser connection can't be
 * trusted: sockets dropped while the tab sat in the background, a frozen tab whose open fetch
 * stops delivering after it resumes, proxy and idle cuts. A reply only counts as finished when
 * its stream sent `message_stop`/`error`, or the server says the row is no longer GENERATING.
 * Any other end re-attaches through GET .../messages/stream, which replays the run from
 * `message_start`, so a fresh event handler rebuilds the whole bubble. The backend sends a
 * `heartbeat` event after 5 s without other events, so a live stream is never silent for long.
 *
 * Plain state machine: React state, the query cache and the network come in through `deps`,
 * so the retry rules run without a browser.
 */

export type StreamKind = 'send' | 'regenerate' | 'reattach';

/** How a stream's request ended: the transport outcome plus its idle timeout. */
export interface StreamEnd {
    reason: 'eof' | 'aborted' | 'network_error' | 'idle_timeout';
    responseStarted: boolean;
}

export interface StreamTarget {
    agentId: string;
    sessionId: string;
    /** Bubble the run writes into: a temp id until `message_start` (or adoption) swaps in the server id. */
    assistantId: string;
    /** User message that started the run, so a replayed `message_start` can rename its temp row too. */
    userMessageId?: string;
    /** Text that was sent, to find the run on the server when `message_start` never arrived. */
    userText?: string;
    /** When the send/regenerate started (ms epoch): a temp bubble only adopts a run created after it. */
    startedAt?: number;
    /** Error text from a request that failed before the server answered, shown if the send turns out lost. */
    failureMessage?: string;
}

/** Why the supervisor let go of a stream; a released stream's frames and end are ignored. */
export type ReleaseReason =
    | 'user_stop'
    | 'superseded'
    | 'stalled'
    | 'failed'
    | 'foreign'
    | 'session_changed'
    | 'unmounted';

export interface StreamRecord {
    readonly kind: StreamKind;
    readonly target: StreamTarget;
    readonly controller: AbortController;
    /** Pass to `createEventHandler` as `cancelledRef`: flips once the stream no longer owns its bubble. */
    readonly cancelledRef: { current: boolean };
    lastActivityAt: number;
    /** Any bytes arrived. Before that a send may just be waiting on a slow backend, so it is never called stalled. */
    receivedData: boolean;
    /** Reply events received (heartbeats excluded). */
    frames: number;
    /** A re-attach replays from `message_start`; without it the server's buffer no longer covers the run. */
    sawMessageStart: boolean;
    sawTerminal: boolean;
    released: ReleaseReason | null;
}

/** What a stream's owner does with its own UI state once the request ends. */
export type StreamVerdict =
    /** `message_stop`/`error` arrived: finalize the bubble. */
    | 'completed'
    /** Ended early; the supervisor is recovering the run, so leave the UI alone. */
    | 'interrupted'
    /** Something newer owns the bubble: do nothing. */
    | 'released';

export type ServerRunState =
    | { status: 'generating'; assistantId: string }
    | { status: 'finished'; assistantId: string }
    /** No row for the run yet. `userSeen`: the sent message is on the server, so the reply row should follow. */
    | { status: 'missing'; userSeen?: boolean }
    | { status: 'unreachable' };

/** How a recovered run ended. */
export type RecoveryEnd =
    /** The re-attached stream sent `message_stop`/`error`. */
    | 'completed'
    /** The server row is final (or gone); the refreshed cache carries it. */
    | 'server'
    /** The server says GENERATING but has no stream to attach to; the row is polled quietly. */
    | 'not_live'
    /** Retries ran out. */
    | 'gave_up'
    /** The send never reached the server. */
    | 'lost';

export type LifecycleEvent = 'hidden' | 'visible' | 'frozen' | 'resumed' | 'online';

export interface StreamSupervisorDeps {
    now(): number;
    setTimer(callback: () => void, ms: number): unknown;
    clearTimer(handle: unknown): void;
    /** 0..1, spreads retries so many tabs don't reconnect in lockstep. */
    random(): number;
    isPageHidden(): boolean;
    /** Fresh page-1 read for the target's session. Also refreshes the messages cache. */
    fetchServerState(target: StreamTarget): Promise<ServerRunState>;
    /** Whether the messages cache still holds this run as GENERATING (a snapshot taken mid-stream). */
    isGeneratingInCache(target: StreamTarget): boolean;
    /** Open GET .../messages/stream into the record's bubble; resolves when the request ends. */
    reattach(record: StreamRecord): Promise<StreamEnd>;
    /** Re-point the bubble at the server row of a run whose `message_start` never arrived. */
    adoptAssistantId(fromId: string, toId: string): void;
    /** Hold the composer lock and show the bubble as streaming while the run is recovered. */
    showRecovering(target: StreamTarget): void;
    /** Recovery is over. `ownsLock` is false when a newer stream already holds the composer lock. */
    finishRecovery(target: StreamTarget, end: RecoveryEnd, ownsLock: boolean): void;
    /** Back on the page with nothing attached: re-read the open session if it may still be generating. */
    revalidate(): void;
}

/** Back-off before the n-th retry that made no progress. */
const RETRY_DELAYS_MS = [0, 1_000, 2_000, 4_000, 8_000, 15_000, 30_000];
/** Tries without progress before giving up (about 2.5 minutes with the delays above). */
const MAX_ATTEMPTS = 10;
/** A re-attach that streamed something and dropped again retries almost at once. */
const PROGRESS_RETRY_MS = 250;
/** Re-attaches in a row that got zero frames: the server has no live stream for the run. */
const EMPTY_ATTACH_LIMIT = 3;
/** Page-1 reads in a row that show neither the run's row nor a GENERATING one. */
const MISSING_READ_LIMIT = 2;
/** A recovery pass still running after this long is assumed wedged and replaced on the next recheck. */
const STUCK_PASS_MS = 45_000;
/** After the page comes back, an open stream that delivers nothing for this long is stalled (heartbeats come every 5 s). */
const STALL_PROBE_MS = 6_000;
/** An open stream silent this long (six missed heartbeats) is dead even without a lifecycle event. */
const SILENCE_LIMIT_MS = 30_000;
/** Hidden at least this long counts as a suspect return; shorter flicks don't touch the stream. */
const SUSPECT_HIDDEN_MS = 5_000;
/** Requests fired the instant a page comes back can fail on WebKit, so give it a moment first. */
const RESUME_SETTLE_MS = 500;
/** Watchdog cadence while a stream is open; a much longer gap means the machine slept or the tab froze. */
const WATCHDOG_INTERVAL_MS = 10_000;
const WATCHDOG_GAP_MS = 30_000;
/** Re-read a finished run until the backend stops reporting it GENERATING. */
const COMPLETION_SYNC_DELAYS_MS = [1_500, 4_000, 10_000];
/** Poll a run the server calls GENERATING but can't stream, for about five minutes. */
const NOT_LIVE_WATCH_DELAYS_MS = [5_000, 10_000, 20_000, 30_000, 60_000, 60_000, 60_000, 60_000];

interface Recovery {
    readonly target: StreamTarget;
    /** Id the recovery started with, also marked finished when the run ends under a renamed id. */
    readonly initialAssistantId: string;
    readonly responseStarted: boolean;
    /** The stalled POST this recovery replaces, while it is still open: its reply row may not exist yet. */
    original: StreamRecord | null;
    attachFirst: boolean;
    attempts: number;
    emptyAttaches: number;
    missingReads: number;
    /** Most reply frames any stream of this run delivered; a replay only counts as progress above it. */
    bestFrames: number;
    running: boolean;
    passStartedAt: number;
    timer: unknown;
}

interface Watch {
    readonly target: StreamTarget;
    readonly delays: readonly number[];
    step: number;
    timer: unknown;
}

type MessageRow = {
    id: string;
    role?: string;
    status?: string;
    parent_message_id?: string | null;
    content?: unknown;
    created_at?: string;
};

/** Client and server clocks differ; a row this much older than the send can still be its run. */
const CLOCK_SKEW_MS = 2 * 60_000;

const isAssistantRow = (row: MessageRow) => String(row.role ?? '').toUpperCase() === 'ASSISTANT';
const isUserRow = (row: MessageRow) => String(row.role ?? '').toUpperCase() === 'USER';

function rowText(row: MessageRow): string {
    if (typeof row.content === 'string') return row.content.trim();
    if (!Array.isArray(row.content)) return '';
    return row.content
        .map((item) => (typeof (item as { text?: unknown })?.text === 'string' ? (item as { text: string }).text : ''))
        .join('')
        .trim();
}

/** Newest assistant row the server still reports GENERATING, from page 1 of the messages list (newest first). */
export function newestGeneratingAssistantId(rows: ReadonlyArray<MessageRow>): string | null {
    return rows.find((row) => isAssistantRow(row) && row.status === 'GENERATING')?.id ?? null;
}

/**
 * Read where a run stands from page 1 of the messages list (newest first). A bubble that never
 * got `message_start` still has a temp id, so its run is the newest GENERATING assistant row.
 */
export function readRunState(
    rows: ReadonlyArray<MessageRow>,
    target: Pick<StreamTarget, 'assistantId' | 'userMessageId' | 'userText' | 'startedAt'>,
): ServerRunState {
    const assistants = rows.filter(isAssistantRow);
    const own = assistants.find((row) => row.id === target.assistantId);
    if (own) {
        return own.status === 'GENERATING'
            ? { status: 'generating', assistantId: own.id }
            : { status: 'finished', assistantId: own.id };
    }
    // Still a temp id (message_start never arrived): find this send's run without adopting an older one.
    const recent = (row: MessageRow) => {
        if (target.startedAt == null || !row.created_at) return true;
        const createdAt = Date.parse(row.created_at);
        return !Number.isFinite(createdAt) || createdAt >= target.startedAt - CLOCK_SKEW_MS;
    };
    let userId = target.userMessageId && !target.userMessageId.startsWith('temp-') ? target.userMessageId : null;
    const sentText = target.userText?.trim();
    if (!userId && sentText) {
        // The newest user row with the sent text is this send's message.
        userId = rows.find((row) => isUserRow(row) && recent(row) && rowText(row) === sentText)?.id ?? null;
    }
    if (userId) {
        const reply = assistants.find((row) => row.parent_message_id === userId);
        if (reply) {
            return reply.status === 'GENERATING'
                ? { status: 'generating', assistantId: reply.id }
                : { status: 'finished', assistantId: reply.id };
        }
        if (rows.some((row) => row.id === userId)) return { status: 'missing', userSeen: true };
    }
    const generating = assistants.find((row) => row.status === 'GENERATING' && recent(row));
    if (generating) return { status: 'generating', assistantId: generating.id };
    return { status: 'missing' };
}

export class StreamSupervisor {
    private readonly deps: StreamSupervisorDeps;
    private mounted = false;
    private sessionId: string | null = null;
    /** The stream currently writing into a bubble. */
    private active: StreamRecord | null = null;
    /** The run that holds the composer lock (isTyping / streamingMessageId). */
    private lockTarget: StreamTarget | null = null;
    private recovery: Recovery | null = null;
    private readonly watches = new Map<string, Watch>();
    /** Runs known to be over (end event, final server row, Stop): never re-attached automatically. */
    private readonly finished = new Set<string>();
    /** Runs recovery found not live or gave up on; skipped by auto-attach. */
    private readonly parked = new Set<string>();
    /** Last run recovery gave up on; retried with a fresh read when the user comes back or the network does. */
    private gaveUp: { target: StreamTarget; responseStarted: boolean } | null = null;
    /** The backend sent a heartbeat on some stream, so a long silence really means a dead connection. */
    private heartbeatsSeen = false;
    private lastResumeAt = -Infinity;
    private hiddenSince: number | null = null;
    private frozen = false;
    private probeTimer: unknown = null;
    private revalidateTimer: unknown = null;
    private watchdogTimer: unknown = null;
    private lastTickAt = 0;

    constructor(deps: StreamSupervisorDeps) {
        this.deps = deps;
    }

    mount() {
        this.mounted = true;
        if (this.hiddenSince == null && this.deps.isPageHidden()) this.hiddenSince = this.deps.now();
    }

    unmount() {
        this.mounted = false;
        if (this.active) this.release(this.active, 'unmounted');
        this.cancelRecovery('unmounted');
        this.lockTarget = null;
        for (const watch of [...this.watches.values()]) this.clearWatch(watch);
        if (this.probeTimer != null) this.deps.clearTimer(this.probeTimer);
        this.probeTimer = null;
        if (this.revalidateTimer != null) this.deps.clearTimer(this.revalidateTimer);
        this.revalidateTimer = null;
        this.syncWatchdog();
    }

    /** The UI switched session: let go of streams and recovery that belong to another one. */
    setSession(sessionId: string | null) {
        if (sessionId === this.sessionId) return;
        this.sessionId = sessionId;
        const belongs = (target: StreamTarget) => target.sessionId === sessionId;
        if (this.active && !belongs(this.active.target)) this.release(this.active, 'session_changed');
        if (this.recovery && !belongs(this.recovery.target)) this.cancelRecovery('session_changed');
        if (this.lockTarget && !belongs(this.lockTarget)) this.lockTarget = null;
        if (this.gaveUp && !belongs(this.gaveUp.target)) this.gaveUp = null;
        // A new chat visit may re-attach once more (as before): a finished run whose row still says
        // GENERATING replays to its end from the server's buffer instead of spinning on a stale row.
        this.finished.clear();
        this.parked.clear();
        for (const watch of [...this.watches.values()]) {
            if (!belongs(watch.target)) this.clearWatch(watch);
        }
    }

    /**
     * Track a stream that now owns its bubble. A send or regenerate also takes the composer lock
     * and replaces any recovery; a stream that already held the bubble is let go.
     */
    begin(kind: StreamKind, target: StreamTarget): StreamRecord {
        if (this.active) this.release(this.active, 'superseded');
        if (kind !== 'reattach') {
            this.cancelRecovery('superseded');
            this.lockTarget = target;
        }
        this.parked.delete(target.assistantId);
        const record: StreamRecord = {
            kind,
            target,
            controller: new AbortController(),
            cancelledRef: { current: false },
            lastActivityAt: this.deps.now(),
            receivedData: false,
            frames: 0,
            sawMessageStart: false,
            sawTerminal: false,
            released: null,
        };
        if (!this.mounted) {
            record.released = 'unmounted';
            record.cancelledRef.current = true;
            return record;
        }
        this.active = record;
        this.syncWatchdog();
        return record;
    }

    /** Bytes arrived, including SSE comments the event parser drops. */
    noteActivity(record: StreamRecord) {
        if (record.released) return;
        record.lastActivityAt = this.deps.now();
        record.receivedData = true;
    }

    /**
     * Count a parsed SSE frame. Returns false when the frame must not reach the event handler:
     * the stream was released, or it is a keep-alive.
     */
    noteEvent(record: StreamRecord, event: unknown): boolean {
        if (record.released) return false;
        record.lastActivityAt = this.deps.now();
        record.receivedData = true;
        const { type, id, message } = (event ?? {}) as { type?: unknown; id?: unknown; message?: { id?: unknown } };
        if (type === 'heartbeat') {
            this.heartbeatsSeen = true;
            return false;
        }
        if (type === 'message_start' && record.kind === 'reattach') {
            const runId = message?.id ?? id;
            // GET .../stream is session-scoped: a replay of another run must not rename this bubble.
            if (typeof runId === 'string' && runId !== record.target.assistantId && !record.target.assistantId.startsWith('temp-')) {
                this.release(record, 'foreign');
                return false;
            }
        }
        record.frames += 1;
        if (type === 'message_start') record.sawMessageStart = true;
        if (type === 'message_stop' || type === 'error') record.sawTerminal = true;
        return true;
    }

    /** `message_start` swapped the bubble's temp id for the server id. */
    noteAssistantId(record: StreamRecord, assistantId: string) {
        record.target.assistantId = assistantId;
    }

    /** A send or regenerate request ended; tells its owner what to do with the UI. */
    end(record: StreamRecord, end: StreamEnd): StreamVerdict {
        if (this.active === record) this.active = null;
        if (this.recovery?.original === record) this.recovery.original = null;
        this.syncWatchdog();
        if (record.released) return 'released';
        if (record.sawTerminal) {
            this.markFinished(record.target.assistantId);
            return 'completed';
        }
        if (end.reason === 'aborted') {
            // Aborted by its owner (a choice reply, the missing-session retry), which handles what comes next.
            record.released = 'superseded';
            record.cancelledRef.current = true;
            return 'released';
        }
        this.startRecovery(record.target, { responseStarted: end.responseStarted, frames: record.frames });
        return 'interrupted';
    }

    /** The request failed outright (an HTTP error its owner reports); nothing to recover. */
    abandon(record: StreamRecord) {
        this.release(record, 'failed');
    }

    /** Clear the composer lock for a finished send/regenerate unless a newer run holds it. */
    releaseLock(record: StreamRecord): boolean {
        if (this.lockTarget !== record.target) return false;
        this.lockTarget = null;
        return true;
    }

    /**
     * A run finished. When the cache holds a mid-stream GENERATING snapshot of it, or the connection
     * dropped after the end event (trailing frames such as suggestions may be missing), re-read the
     * row until the backend has saved it.
     */
    syncAfterCompletion(target: StreamTarget, closedCleanly: boolean) {
        if (closedCleanly && !this.deps.isGeneratingInCache(target)) return;
        this.watchUntilFinal({ ...target }, COMPLETION_SYNC_DELAYS_MS);
    }

    /** The user pressed Stop: drop the stream and any recovery, and never re-attach this run. */
    stopRun(): StreamTarget | null {
        const target = this.active?.target ?? this.recovery?.target ?? this.lockTarget;
        if (this.active) this.release(this.active, 'user_stop');
        this.cancelRecovery('user_stop');
        this.lockTarget = null;
        if (target) this.markFinished(target.assistantId);
        return target;
    }

    /** Stop failed, so the run is still going: pick it back up. */
    resumeAfterFailedStop(target: StreamTarget) {
        this.finished.delete(target.assistantId);
        this.reconnect(target);
    }

    /** Manual retry after recovery gave up. */
    reconnect(target: StreamTarget) {
        this.parked.delete(target.assistantId);
        if (this.gaveUp?.target.assistantId === target.assistantId) this.gaveUp = null;
        if (!this.canRecover(target)) return;
        this.startRecovery(target, { responseStarted: true });
    }

    /** Fresh server data shows this run GENERATING with nothing attached to it. */
    autoAttach(target: StreamTarget) {
        if (!this.canRecover(target)) return;
        if (this.finished.has(target.assistantId) || this.parked.has(target.assistantId)) return;
        this.startRecovery(target, { responseStarted: true, attachFirst: true });
    }

    onLifecycle(event: LifecycleEvent) {
        if (!this.mounted) return;
        const now = this.deps.now();
        if (event === 'hidden') {
            if (this.hiddenSince == null) this.hiddenSince = now;
            return;
        }
        if (event === 'frozen') {
            this.frozen = true;
            return;
        }
        const hiddenFor = this.hiddenSince == null ? 0 : now - this.hiddenSince;
        const wasFrozen = this.frozen;
        if (event !== 'online') this.lastResumeAt = now;
        if (event === 'visible') this.hiddenSince = null;
        if (event !== 'online') this.frozen = false;
        const suspect = event !== 'visible' || wasFrozen || hiddenFor >= SUSPECT_HIDDEN_MS;
        if (suspect) this.recheck();
    }

    private canRecover(target: StreamTarget) {
        return this.mounted && target.sessionId === this.sessionId && !this.active && !this.recovery;
    }

    /** The page is back (or the network is): verify whatever we believe is streaming. */
    private recheck() {
        const { recovery, active } = this;
        if (recovery) {
            if (recovery.running && this.deps.now() - recovery.passStartedAt >= STUCK_PASS_MS && active?.kind !== 'reattach') {
                // A pass wedged on a request that never settles: start over so its late result is ignored.
                this.startRecovery(recovery.target, { responseStarted: recovery.responseStarted, frames: recovery.bestFrames, delayMs: RESUME_SETTLE_MS });
                return;
            }
            recovery.attempts = 0;
            if (active?.kind === 'reattach') this.probe(active);
            else if (!recovery.running) this.schedule(recovery, RESUME_SETTLE_MS);
            return;
        }
        if (active) {
            this.probe(active);
            return;
        }
        const gaveUp = this.gaveUp;
        if (gaveUp && gaveUp.target.sessionId === this.sessionId) {
            // We gave up while the page or network was away; read the run again instead of trusting the cache.
            this.gaveUp = null;
            this.parked.delete(gaveUp.target.assistantId);
            this.startRecovery(gaveUp.target, { responseStarted: gaveUp.responseStarted, delayMs: RESUME_SETTLE_MS });
            return;
        }
        if (this.revalidateTimer != null) this.deps.clearTimer(this.revalidateTimer);
        this.revalidateTimer = this.deps.setTimer(() => {
            this.revalidateTimer = null;
            if (this.mounted && !this.active && !this.recovery) this.deps.revalidate();
        }, RESUME_SETTLE_MS);
    }

    /** A frozen tab's open fetch can stay silent forever after it resumes; give it a moment, then replace it. */
    private probe(record: StreamRecord) {
        if (this.probeTimer != null) this.deps.clearTimer(this.probeTimer);
        const since = this.deps.now();
        this.probeTimer = this.deps.setTimer(() => {
            this.probeTimer = null;
            if (this.active !== record || record.released) return;
            if (record.lastActivityAt > since) return;
            // An ended reply may still be sending suggestions; the silence rule closes it if it hangs.
            if (record.sawTerminal) return;
            this.stall(record);
        }, STALL_PROBE_MS);
    }

    /** A send/regenerate that has received nothing may just be waiting on the backend; only re-attaches can stall then. */
    private canStall(record: StreamRecord) {
        return record.kind === 'reattach' || record.receivedData;
    }

    private stall(record: StreamRecord) {
        if (!this.canStall(record)) return;
        if (record.sawTerminal) {
            // The reply already ended and only the close is stuck: closing it can't hurt the run,
            // and its owner then finishes it as completed.
            record.controller.abort();
            return;
        }
        this.release(record, 'stalled');
        // A stalled re-attach belongs to a recovery, which retries once its abort lands.
        if (record.kind === 'reattach') return;
        this.startRecovery(record.target, { responseStarted: true, frames: record.frames });
        // The POST stays open (it is never aborted); until it closes its reply row may not exist yet.
        if (this.recovery) this.recovery.original = record;
    }

    private release(record: StreamRecord, reason: ReleaseReason) {
        if (record.released) return;
        record.released = reason;
        record.cancelledRef.current = true;
        if (this.active === record) this.active = null;
        // Dropping a read-only re-attach is always safe; POSTs are left to finish server-side.
        if (record.kind === 'reattach') record.controller.abort();
        this.syncWatchdog();
    }

    private markFinished(assistantId: string) {
        this.finished.add(assistantId);
        this.parked.delete(assistantId);
    }

    private startRecovery(
        target: StreamTarget,
        options: { responseStarted: boolean; attachFirst?: boolean; frames?: number; delayMs?: number },
    ) {
        this.cancelRecovery('superseded');
        const watch = this.watches.get(target.assistantId);
        if (watch) this.clearWatch(watch);
        const recovery: Recovery = {
            target,
            initialAssistantId: target.assistantId,
            responseStarted: options.responseStarted,
            original: null,
            attachFirst: options.attachFirst ?? false,
            attempts: 0,
            emptyAttaches: 0,
            missingReads: 0,
            bestFrames: options.frames ?? 0,
            running: false,
            passStartedAt: 0,
            timer: null,
        };
        this.recovery = recovery;
        this.lockTarget = target;
        this.deps.showRecovering(target);
        const justResumed = this.deps.now() - this.lastResumeAt < RESUME_SETTLE_MS * 3;
        this.schedule(recovery, options.delayMs ?? (justResumed ? RESUME_SETTLE_MS : 0));
    }

    private cancelRecovery(reason: ReleaseReason) {
        const recovery = this.recovery;
        if (!recovery) return;
        this.recovery = null;
        if (recovery.timer != null) this.deps.clearTimer(recovery.timer);
        recovery.timer = null;
        if (this.active?.kind === 'reattach' && this.active.target === recovery.target) {
            this.release(this.active, reason);
        }
    }

    private schedule(recovery: Recovery, delayMs: number) {
        if (recovery.timer != null) this.deps.clearTimer(recovery.timer);
        recovery.timer = this.deps.setTimer(() => {
            recovery.timer = null;
            void this.runRecovery(recovery);
        }, delayMs);
    }

    private async runRecovery(recovery: Recovery) {
        if (this.recovery !== recovery || recovery.running) return;
        recovery.running = true;
        recovery.passStartedAt = this.deps.now();
        try {
            await this.recoverOnce(recovery);
        } catch {
            if (this.recovery === recovery) this.retryLater(recovery);
        } finally {
            recovery.running = false;
        }
    }

    private async recoverOnce(recovery: Recovery) {
        const { target } = recovery;
        if (!recovery.attachFirst) {
            const state = await this.deps.fetchServerState(target);
            if (this.recovery !== recovery) return;
            if (state.status === 'unreachable') {
                if (!recovery.responseStarted && !recovery.original) {
                    // The send got no answer and the server can't be reached: report it instead of spinning.
                    recovery.missingReads += 1;
                    if (recovery.missingReads >= MISSING_READ_LIMIT) {
                        this.endRecovery(recovery, 'lost');
                        return;
                    }
                }
                this.retryLater(recovery);
                return;
            }
            if (state.status === 'missing') {
                if (recovery.original || state.userSeen) {
                    // The run exists or is about to (its POST is still open, or its message was taken): wait for its row.
                    this.retryLater(recovery);
                    return;
                }
                recovery.missingReads += 1;
                if (recovery.missingReads >= MISSING_READ_LIMIT) {
                    this.endRecovery(recovery, recovery.responseStarted ? 'server' : 'lost');
                } else {
                    this.retryLater(recovery);
                }
                return;
            }
            this.adopt(target, state.assistantId);
            if (state.status === 'finished') {
                this.markFinished(target.assistantId);
                this.endRecovery(recovery, 'server');
                return;
            }
        }
        recovery.attachFirst = false;

        const record = this.begin('reattach', target);
        let end: StreamEnd;
        try {
            end = await this.deps.reattach(record);
        } catch {
            // An HTTP error means there is nothing to attach to right now.
            end = { reason: 'network_error', responseStarted: false };
        }
        if (this.active === record) this.active = null;
        this.syncWatchdog();
        if (this.recovery !== recovery) return;

        // Without message_start this was no replay (the run finished over 30 minutes ago, or a very
        // long reply outgrew the buffer): its frames were not applied, so treat it like an empty attach
        // and let the next status read load the saved message.
        if (record.sawMessageStart && record.sawTerminal) {
            this.markFinished(target.assistantId);
            this.endRecovery(recovery, 'completed');
            return;
        }
        // A replay re-sends everything from message_start, so only frames beyond the best earlier
        // stream are progress. A run that died at GENERATING replays the same frames every time.
        if (record.sawMessageStart && record.frames > recovery.bestFrames) {
            recovery.bestFrames = record.frames;
            recovery.attempts = 0;
            recovery.emptyAttaches = 0;
            this.schedule(recovery, PROGRESS_RETRY_MS);
            return;
        }
        const answeredEmpty =
            record.released === 'foreign' || (end.reason === 'eof' && end.responseStarted) || record.sawMessageStart;
        if (!answeredEmpty) {
            // The re-attach itself failed (network, 5xx, idle timeout): a retry, not proof the run is gone.
            this.retryLater(recovery);
            return;
        }
        recovery.emptyAttaches += 1;
        if (recovery.emptyAttaches >= EMPTY_ATTACH_LIMIT) {
            this.parked.add(target.assistantId);
            this.endRecovery(recovery, 'not_live');
            this.watchUntilFinal({ ...target }, NOT_LIVE_WATCH_DELAYS_MS);
            return;
        }
        this.retryLater(recovery);
    }

    private retryLater(recovery: Recovery) {
        recovery.attempts += 1;
        if (recovery.attempts >= MAX_ATTEMPTS) {
            this.parked.add(recovery.target.assistantId);
            this.gaveUp = { target: recovery.target, responseStarted: recovery.responseStarted };
            this.endRecovery(recovery, 'gave_up');
            return;
        }
        const delay = RETRY_DELAYS_MS[Math.min(recovery.attempts, RETRY_DELAYS_MS.length - 1)];
        this.schedule(recovery, Math.round(delay * (0.8 + 0.4 * this.deps.random())));
    }

    private endRecovery(recovery: Recovery, end: RecoveryEnd) {
        if (this.recovery === recovery) this.recovery = null;
        if (recovery.timer != null) this.deps.clearTimer(recovery.timer);
        recovery.timer = null;
        const ownsLock = this.lockTarget === recovery.target;
        if (ownsLock) this.lockTarget = null;
        if ((end === 'completed' || end === 'server') && recovery.initialAssistantId !== recovery.target.assistantId) {
            this.markFinished(recovery.initialAssistantId);
        }
        this.deps.finishRecovery(recovery.target, end, ownsLock);
        if (end === 'completed') this.syncAfterCompletion(recovery.target, true);
    }

    private adopt(target: StreamTarget, serverId: string) {
        if (serverId === target.assistantId) return;
        const fromId = target.assistantId;
        target.assistantId = serverId;
        this.deps.adoptAssistantId(fromId, serverId);
    }

    /** Re-read a row on a slow schedule until the server stops reporting it GENERATING. */
    private watchUntilFinal(target: StreamTarget, delays: readonly number[]) {
        if (!this.mounted) return;
        const existing = this.watches.get(target.assistantId);
        if (existing) this.clearWatch(existing);
        const watch: Watch = { target, delays, step: 0, timer: null };
        this.watches.set(target.assistantId, watch);
        this.scheduleWatch(watch);
    }

    private scheduleWatch(watch: Watch) {
        const delay = watch.delays[watch.step];
        if (delay === undefined) {
            this.watches.delete(watch.target.assistantId);
            return;
        }
        watch.timer = this.deps.setTimer(() => {
            watch.timer = null;
            void this.runWatch(watch);
        }, delay);
    }

    private async runWatch(watch: Watch) {
        let state: ServerRunState;
        try {
            state = await this.deps.fetchServerState(watch.target);
        } catch {
            state = { status: 'unreachable' };
        }
        if (this.watches.get(watch.target.assistantId) !== watch) return;
        if (state.status === 'finished' || state.status === 'missing') {
            this.watches.delete(watch.target.assistantId);
            if (state.status === 'finished') this.markFinished(state.assistantId);
            return;
        }
        watch.step += 1;
        this.scheduleWatch(watch);
    }

    private clearWatch(watch: Watch) {
        if (watch.timer != null) this.deps.clearTimer(watch.timer);
        watch.timer = null;
        if (this.watches.get(watch.target.assistantId) === watch) this.watches.delete(watch.target.assistantId);
    }

    /** Timers stop while the machine sleeps; a tick that lands long after the last one means we just woke. */
    private syncWatchdog() {
        const needed = this.mounted && this.active != null;
        if (needed && this.watchdogTimer == null) {
            this.lastTickAt = this.deps.now();
            this.tickWatchdog();
        } else if (!needed && this.watchdogTimer != null) {
            this.deps.clearTimer(this.watchdogTimer);
            this.watchdogTimer = null;
        }
    }

    private tickWatchdog() {
        this.watchdogTimer = this.deps.setTimer(() => {
            this.watchdogTimer = null;
            const now = this.deps.now();
            const gap = now - this.lastTickAt;
            this.lastTickAt = now;
            const active = this.active;
            if (!active) return;
            if (gap >= WATCHDOG_GAP_MS) {
                // Just woke from sleep or a freeze: data buffered meanwhile may still be on its way.
                if (!this.deps.isPageHidden()) this.probe(active);
            } else if (
                // Closing an already-ended reply is always safe; anything else needs proof the backend sends heartbeats.
                (this.heartbeatsSeen || active.sawTerminal) &&
                this.probeTimer == null &&
                this.canStall(active) &&
                now - active.lastActivityAt >= SILENCE_LIMIT_MS
            ) {
                this.stall(active);
            }
            if (this.active && this.watchdogTimer == null) this.tickWatchdog();
        }, WATCHDOG_INTERVAL_MS);
    }
}
